import { computed, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  COMISSOES_MOCK_PROFILES,
  COMISSOES_MOCK_PROFILE_DEFAULT,
  type ComissoesMockProfile,
  type ComissoesMockProfileId,
} from './comissoes-mock-profiles';

const MOCK_PORTFOLIO_ID = '11111111-1111-1111-1111-111111111111';

function claimsFromProfile(profile: ComissoesMockProfile) {
  return {
    preferred_username: profile.username,
    email: `${profile.username}@pmdf.df.gov.br`,
    portfolio_id: MOCK_PORTFOLIO_ID,
    clearance: profile.clearance,
    realm_access: { roles: profile.roles },
    roles: profile.roles,
  };
}

/**
 * Substituto do {@link AuthService} no modo mock (npm run start:mock). Não usa Keycloak/OIDC:
 * o usuário já está "autenticado" com perfil institucional selecionável (Onda 4 RBAC).
 * Registrado condicionalmente em app.config.ts apenas quando environment.mock = true.
 */
export class MockAuthService {
  private readonly perfilId = signal<ComissoesMockProfileId>(COMISSOES_MOCK_PROFILE_DEFAULT);

  private readonly perfilAtual = computed(
    () =>
      COMISSOES_MOCK_PROFILES.find((p) => p.id === this.perfilId()) ??
      COMISSOES_MOCK_PROFILES[0],
  );

  readonly isAuthenticated = computed(() => true);
  readonly claims = computed(() => claimsFromProfile(this.perfilAtual()));
  readonly username = computed(() => this.perfilAtual().username);
  readonly portfolioId = computed(() => MOCK_PORTFOLIO_ID);
  readonly clearance = computed(() => this.perfilAtual().clearance);
  readonly perfisDisponiveis = COMISSOES_MOCK_PROFILES;
  readonly perfilSelecionado = this.perfilId.asReadonly();

  checkAuth(): Observable<{ isAuthenticated: boolean }> {
    return of({ isAuthenticated: true });
  }

  login(): void {
    // No-op no modo mock: o usuário já está autenticado.
  }

  logout(): void {
    // No-op no modo mock.
  }

  getAccessToken(): Observable<string> {
    return of('mock-access-token');
  }

  selecionarPerfil(id: ComissoesMockProfileId): void {
    if (COMISSOES_MOCK_PROFILES.some((p) => p.id === id)) {
      this.perfilId.set(id);
    }
  }

  hasAnyRole(requiredRoles: string[]): boolean {
    if (requiredRoles.length === 0) {
      return true;
    }
    const granted = new Set(this.perfilAtual().roles);
    return requiredRoles.some((role) => granted.has(role));
  }
}
